import { LinearGradient } from "expo-linear-gradient";
import { router } from "expo-router";
import React, { useCallback, useMemo, useRef } from "react";
import type { Ref } from "react";
import { Platform, View } from "react-native";
import type { View as RNView } from "react-native";
import { useSharedValue } from "react-native-reanimated";
import { withUnistyles } from "react-native-unistyles";

import Icon from "$src/core/components/atoms/Icon";
import Image from "$src/core/components/atoms/Image/Image";
import Pressable from "$src/core/components/atoms/Pressable";
import Text from "$src/core/components/atoms/Text/Text";
import { TV_PARALLAX } from "$src/core/constants/tv-parallax";
import useResponsiveImage from "$src/core/hooks/use-responsive-image";
import useTranslation from "$src/core/hooks/use-translation";
import type {
  EPGEntryDto,
  EPGLinkedContentDto,
  ImageDto,
} from "$src/core/models/dto";
import programTimeRangeMs from "$src/core/utils/epg/program-time-range-ms";
import pickImageUrl from "$src/core/utils/image/pick-image-url";
import CustomPlatform from "$src/core/utils/platform/platform";

import $, {
  IMAGE_DIMENSIONS,
  useProgramBlockAnimatedStyles,
} from "./EPGProgramBlock.styles";

// Fade into the card: CMS surface on TV; phone/web cards are a tint over the page colour.
const UniLinearGradient = withUnistyles(LinearGradient, (theme) => ({
  colors: [
    "transparent",
    CustomPlatform.select({
      tv: theme.colors.backgroundSurface,
      default: theme.colors.backgroundDefault,
    }),
  ] as const,
}));

/** The centred play button and the row's overflow menu are desktop (web) only. */
const SHOW_DESKTOP_AFFORDANCES = CustomPlatform.select({
  web: true,
  default: false,
});

/** Phone live stills need a scrim; web/TV keep the brighter card. */
const SHOW_LIVE_IMAGE_SCRIM = CustomPlatform.select({
  native: true,
  default: false,
});

type EPGProgramBlockProps = {
  ref?: Ref<RNView>;
  channelId: string;
  entry: EPGEntryDto;
  channelLogo?: ImageDto;
  /** Linear playback target for the currently airing cell on this channel. */
  channelLiveChannel?: EPGLinkedContentDto;
  highlightLivePrograms?: boolean;
  nowMs?: number;
  hasTVPreferredFocus?: boolean;
  onTVFocusZoneEnter?: () => void;
  onTVProgramNavigateToDetail?: (params: {
    channelId: string;
    entryId: string;
  }) => void;
  onTVEpgProgramFocus?: (channelId: string) => void;
  /** Web: report which program cell gained focus so the grid can drive arrow-key navigation. */
  onProgramFocus?: (channelId: string, entryId: string) => void;
};

const clamp01 = (value: number) => Math.min(1, Math.max(0, value));

/* A Select that arrives this soon after a programmatic focus did not start on the card. */
const STRAY_PRESS_AFTER_FOCUS_MS = 400;

const EPGProgramBlockInner = ({
  ref: forwardedRef,
  channelId,
  entry,
  channelLogo,
  channelLiveChannel,
  highlightLivePrograms,
  nowMs,
  hasTVPreferredFocus,
  onTVFocusZoneEnter,
  onTVProgramNavigateToDetail,
  onTVEpgProgramFocus,
  onProgramFocus,
}: EPGProgramBlockProps) => {
  const { d, t } = useTranslation();
  const { item, title } = entry;

  const range = programTimeRangeMs(entry);
  const isLive =
    !!highlightLivePrograms &&
    nowMs != null &&
    range != null &&
    nowMs >= range.start &&
    nowMs < range.end;
  const isPast =
    !!highlightLivePrograms &&
    nowMs != null &&
    range != null &&
    nowMs >= range.end;

  const progressRatio = isLive
    ? clamp01((nowMs - range.start) / (range.end - range.start))
    : 0;
  const remainingMinutes = isLive
    ? Math.max(1, Math.ceil((range.end - nowMs) / 60000))
    : 0;

  const isFocused = useSharedValue(false);
  const { focusStyle } = useProgramBlockAnimatedStyles({ isFocused, isLive });

  // Falls back to the channel logo, then to the shared Image placeholder.
  const img = item.image ?? channelLogo;
  const imageUri = img
    ? pickImageUrl(img, ["landscape", "square", "portrait"])
    : undefined;
  const source = useResponsiveImage(imageUri, {
    dimensions: IMAGE_DIMENSIONS.live,
  });

  const startLabel = item.startDate ? d(item.startDate, "time-hm") : undefined;
  const endLabel = item.endDate ? d(item.endDate, "time-hm") : undefined;

  // Null for a live cell whose channel has no linear target: nothing to open.
  const target = isLive ? channelLiveChannel : item;
  const href = target
    ? (`/${target.type}/${target.id}${isLive ? "/play" : ""}` as const)
    : null;
  const isInteractive = href != null;

  /* Announce what the card shows: the badges and progress bar are accessible={false}. */
  const accessibilityLabel = useMemo(
    () =>
      [
        isLive ? t("COMMON/LIVE") : undefined,
        [startLabel, endLabel].filter(Boolean).join(" – ") || undefined,
        remainingMinutes > 0
          ? t("EPG/REMAINING_MINUTES", { minutes: remainingMinutes })
          : undefined,
        title,
      ]
        .filter(Boolean)
        .join(". "),
    [isLive, t, startLabel, endLabel, remainingMinutes, title],
  );

  const focusedAtRef = useRef(0);

  const handleFocus = useCallback(() => {
    // Stamp only a programmatic focus, so a real D-pad move can Select right away.
    if (hasTVPreferredFocus) {
      focusedAtRef.current = Date.now();
    }
    isFocused.set(true);
    onTVFocusZoneEnter?.();
    onProgramFocus?.(channelId, entry.id);
    // Report on every focus, not once per block: the page de-dupes the state update.
    onTVEpgProgramFocus?.(channelId);
  }, [
    hasTVPreferredFocus,
    isFocused,
    onTVFocusZoneEnter,
    onProgramFocus,
    onTVEpgProgramFocus,
    channelId,
    entry.id,
  ]);

  const handlePress = useCallback(() => {
    // tvOS delivers Select to whichever view has focus when the press ends.
    if (
      Platform.isTV &&
      Date.now() - focusedAtRef.current < STRAY_PRESS_AFTER_FOCUS_MS
    ) {
      return;
    }

    if (!href) {
      return;
    }

    if (Platform.isTV) {
      onTVProgramNavigateToDetail?.({ channelId, entryId: entry.id });
    }

    router.navigate(href);
  }, [channelId, entry.id, href, onTVProgramNavigateToDetail]);

  const timeAndTitle = (
    <>
      <Text
        accessible={false}
        accessibilityRole="text"
        numberOfLines={1}
        preset="body"
        style={[$.time, isPast && $.past]}
      >
        {startLabel}
      </Text>
      <Text
        accessible={false}
        accessibilityRole="text"
        ellipsizeMode="tail"
        numberOfLines={1}
        preset="body"
        style={[$.title, isPast && $.past]}
      >
        {title}
      </Text>
    </>
  );

  if (isLive) {
    return (
      <Pressable
        accessibilityLabel={accessibilityLabel}
        accessibilityRole={isInteractive ? "button" : undefined}
        ref={forwardedRef}
        style={[$.liveCard, Platform.isTV && focusStyle]}
        hasTVPreferredFocus={hasTVPreferredFocus}
        tvParallaxProperties={TV_PARALLAX.none}
        onPress={handlePress}
        // Android disables focus on a disabled view, and the EPG's landing focus aims at this row.
        disabled={!isInteractive && !Platform.isTV}
        onFocus={handleFocus}
        onBlur={() => isFocused.set(false)}
      >
        <View style={$.liveCardInner}>
          <View style={$.imageWrap}>
            {source ? (
              <Image
                accessibilityIgnoresInvertColors
                accessibilityLabel={title}
                accessibilityRole="image"
                accessible={false}
                contentFit="cover"
                source={source}
                style={$.image}
              />
            ) : null}
            {SHOW_LIVE_IMAGE_SCRIM ? (
              <View style={$.imageScrim} pointerEvents="none" />
            ) : null}
            <UniLinearGradient style={$.imageGradient} pointerEvents="none" />
            {SHOW_DESKTOP_AFFORDANCES ? (
              <View style={$.playButton} pointerEvents="none">
                <Icon name="play" style={$.playIcon} />
              </View>
            ) : null}
            <View style={$.bottomOverlay} pointerEvents="none">
              <View style={$.badgeRow}>
                <View style={$.liveBadge}>
                  <Text
                    accessible={false}
                    accessibilityRole="text"
                    numberOfLines={1}
                    preset="tiny"
                    style={$.liveBadgeText}
                  >
                    {t("COMMON/LIVE")}
                  </Text>
                </View>
                {remainingMinutes > 0 ? (
                  <View style={$.remainingBadge}>
                    <Text
                      accessible={false}
                      accessibilityRole="text"
                      numberOfLines={1}
                      preset="tiny"
                      style={$.remainingBadgeText}
                    >
                      {t("EPG/REMAINING_MINUTES", {
                        minutes: remainingMinutes,
                      })}
                    </Text>
                  </View>
                ) : null}
              </View>
              <View style={$.progressTrack}>
                <View
                  style={[$.progressFill, { width: `${progressRatio * 100}%` }]}
                />
              </View>
            </View>
          </View>
          <View style={$.metaRow}>
            {timeAndTitle}
            {SHOW_DESKTOP_AFFORDANCES ? (
              <Icon name="more" style={$.menuIcon} />
            ) : null}
          </View>
        </View>
      </Pressable>
    );
  }

  return (
    <Pressable
      accessibilityLabel={accessibilityLabel}
      accessibilityRole={isInteractive ? "button" : undefined}
      ref={forwardedRef}
      style={[$.compactRow, Platform.isTV && focusStyle]}
      hasTVPreferredFocus={hasTVPreferredFocus}
      // Magnification would push the focus outline outside the list, which clips it.
      tvParallaxProperties={TV_PARALLAX.none}
      onPress={handlePress}
      disabled={!isInteractive}
      onFocus={handleFocus}
      onBlur={() => isFocused.set(false)}
    >
      {timeAndTitle}
    </Pressable>
  );
};

const EPGProgramBlock = React.memo(EPGProgramBlockInner, (prev, next) => {
  if (prev.entry.id !== next.entry.id) return false;
  if (prev.entry.item.image !== next.entry.item.image) return false;
  if (prev.channelLogo !== next.channelLogo) return false;
  if (prev.channelLiveChannel !== next.channelLiveChannel) return false;
  if (prev.highlightLivePrograms !== next.highlightLivePrograms) return false;
  if (prev.hasTVPreferredFocus !== next.hasTVPreferredFocus) return false;
  if (prev.onTVFocusZoneEnter !== next.onTVFocusZoneEnter) return false;
  if (prev.onTVProgramNavigateToDetail !== next.onTVProgramNavigateToDetail) {
    return false;
  }

  if (prev.onTVEpgProgramFocus !== next.onTVEpgProgramFocus) {
    return false;
  }

  if (prev.onProgramFocus !== next.onProgramFocus) {
    return false;
  }

  if (!next.highlightLivePrograms) {
    return true;
  }

  const range = programTimeRangeMs(next.entry);
  if (!range) {
    return true;
  }

  const prevLive =
    prev.nowMs != null && prev.nowMs >= range.start && prev.nowMs < range.end;
  const nextLive =
    next.nowMs != null && next.nowMs >= range.start && next.nowMs < range.end;

  if (prevLive !== nextLive) {
    return false;
  }

  // Live rows re-render on each tick for the progress bar; others ignore nowMs.
  if (nextLive) {
    return prev.nowMs === next.nowMs;
  }

  return true;
});

export default EPGProgramBlock;
